// Système de logging de sécurité pour EMDR Plus
import { useState, useEffect } from 'react'

class SecurityLogger {
  constructor() {
    this.logs = []
    this.maxLogs = 1000 // Garder les 1000 derniers logs
  }

  // Enregistrer un événement de sécurité
  log(type, data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type,
      data,
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.getSessionId()
    }

    this.logs.push(logEntry)
    
    // Nettoyer les anciens logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs)
    }

    // Log dans la console selon le type
    switch (type) {
      case 'bot_detected':
        console.warn('🤖 Bot détecté:', data)
        break
      case 'rate_limit_exceeded':
        console.warn('⚡ Rate limit dépassé:', data)
        break
      case 'suspicious_email':
        console.warn('📧 Email suspect:', data)
        break
      case 'honeypot_filled':
        console.warn('🍯 Honeypot rempli:', data)
        break
      case 'suspicious_behavior':
        console.warn('🚨 Comportement suspect:', data)
        break
      case 'auth_attempt':
        console.info('🔐 Tentative d\'authentification:', data)
        break
      default:
        console.log('📝 Événement de sécurité:', type, data)
    }

    // Envoyer à un service de monitoring en production
    if (process.env.NODE_ENV === 'production') {
      this.sendToMonitoring(logEntry)
    }
  }

  // Obtenir l'ID de session
  getSessionId() {
    let sessionId = sessionStorage.getItem('security_session_id')
    if (!sessionId) {
      sessionId = 'session_' + Math.random().toString(36).substr(2, 15)
      sessionStorage.setItem('security_session_id', sessionId)
    }
    return sessionId
  }

  // Envoyer les logs à un service de monitoring (à implémenter)
  async sendToMonitoring(logEntry) {
    try {
      // En production, vous pourriez envoyer à un service comme:
      // - Sentry
      // - LogRocket
      // - Votre propre API de logging
      
      // Pour l'instant, on stocke localement
      const existingLogs = JSON.parse(localStorage.getItem('security_logs') || '[]')
      existingLogs.push(logEntry)
      
      // Garder seulement les 100 derniers logs en localStorage
      const recentLogs = existingLogs.slice(-100)
      localStorage.setItem('security_logs', JSON.stringify(recentLogs))
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi des logs:', error)
    }
  }

  // Obtenir les statistiques de sécurité
  getStats() {
    const now = Date.now()
    const oneHour = 60 * 60 * 1000
    const oneDay = 24 * oneHour

    const recentLogs = this.logs.filter(log => 
      now - new Date(log.timestamp).getTime() < oneDay
    )

    const stats = {
      total_events: this.logs.length,
      last_24h: recentLogs.length,
      last_hour: recentLogs.filter(log => 
        now - new Date(log.timestamp).getTime() < oneHour
      ).length,
      by_type: {}
    }

    // Compter par type
    recentLogs.forEach(log => {
      stats.by_type[log.type] = (stats.by_type[log.type] || 0) + 1
    })

    return stats
  }

  // Obtenir les logs récents
  getRecentLogs(hours = 24) {
    const cutoff = Date.now() - (hours * 60 * 60 * 1000)
    return this.logs.filter(log => 
      new Date(log.timestamp).getTime() > cutoff
    )
  }

  // Détecter des patterns suspects
  detectSuspiciousPatterns() {
    const recentLogs = this.getRecentLogs(1) // Dernière heure
    const patterns = []

    // Trop de tentatives d'authentification
    const authAttempts = recentLogs.filter(log => log.type === 'auth_attempt')
    if (authAttempts.length > 10) {
      patterns.push({
        type: 'high_auth_volume',
        count: authAttempts.length,
        severity: 'medium'
      })
    }

    // Détections de bots multiples
    const botDetections = recentLogs.filter(log => log.type === 'bot_detected')
    if (botDetections.length > 5) {
      patterns.push({
        type: 'multiple_bot_detections',
        count: botDetections.length,
        severity: 'high'
      })
    }

    // Emails suspects répétés
    const suspiciousEmails = recentLogs.filter(log => log.type === 'suspicious_email')
    if (suspiciousEmails.length > 3) {
      patterns.push({
        type: 'suspicious_email_pattern',
        count: suspiciousEmails.length,
        severity: 'medium'
      })
    }

    return patterns
  }

  // Exporter les logs pour analyse
  exportLogs() {
    const data = {
      exported_at: new Date().toISOString(),
      stats: this.getStats(),
      patterns: this.detectSuspiciousPatterns(),
      logs: this.logs
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json'
    })
    
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `security-logs-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Nettoyer les logs anciens
  cleanup() {
    const cutoff = Date.now() - (7 * 24 * 60 * 60 * 1000) // 7 jours
    this.logs = this.logs.filter(log => 
      new Date(log.timestamp).getTime() > cutoff
    )
  }
}

// Instance globale
export const securityLogger = new SecurityLogger()

// Fonctions utilitaires
export const logBotDetection = (reason, email, details = {}) => {
  securityLogger.log('bot_detected', {
    reason,
    email,
    ...details
  })
}

export const logAuthAttempt = (email, success = false, details = {}) => {
  securityLogger.log('auth_attempt', {
    email,
    success,
    ...details
  })
}

export const logSuspiciousEmail = (email, reason, details = {}) => {
  securityLogger.log('suspicious_email', {
    email,
    reason,
    ...details
  })
}

export const logRateLimit = (identifier, details = {}) => {
  securityLogger.log('rate_limit_exceeded', {
    identifier,
    ...details
  })
}

// Hook React pour surveiller la sécurité
export const useSecurityMonitoring = () => {
  const [stats, setStats] = useState(null)
  const [patterns, setPatterns] = useState([])

  useEffect(() => {
    const updateStats = () => {
      setStats(securityLogger.getStats())
      setPatterns(securityLogger.detectSuspiciousPatterns())
    }

    updateStats()
    const interval = setInterval(updateStats, 60000) // Mise à jour chaque minute

    return () => clearInterval(interval)
  }, [])

  return {
    stats,
    patterns,
    exportLogs: () => securityLogger.exportLogs(),
    getRecentLogs: (hours) => securityLogger.getRecentLogs(hours)
  }
} 